import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  ContentCopy as DuplicateIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import unifiedApi from '../../utils/unifiedApi';
import { DAYS_OF_WEEK } from '../../utils/scheduleFormat';
import Section, { nativeSelectProps, shrinkLabelProps } from './ScheduleFormSection';
import ExtraGamesSection from './ExtraGamesSection';
import {
  createEmptyBlackout,
  createEmptySlot,
  isRegularSeasonIncluded,
  slotsToPayload,
  templateToSlots,
} from './scheduleFormModel';

const ScheduleConfigForm = ({
  form,
  onChange,
  teams = [],
  venues = [],
  templates = [],
  onTemplatesChanged,
  disabled = false,
}) => {
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [overwriteTemplate, setOverwriteTemplate] = useState(true);
  const [templateSaving, setTemplateSaving] = useState(false);
  const [templateError, setTemplateError] = useState(null);
  const [templateMessage, setTemplateMessage] = useState(null);

  const venueById = useMemo(() => {
    const map = new Map();
    venues.forEach((v) => map.set(String(v.id), v));
    return map;
  }, [venues]);

  const selectedTemplate = templates.find((t) => String(t.id) === String(selectedTemplateId)) || null;

  const update = (patch) => onChange({ ...form, ...patch });

  const toggleTeam = (teamId) => {
    const ids = form.teamIds.includes(teamId)
      ? form.teamIds.filter((id) => id !== teamId)
      : [...form.teamIds, teamId];
    update({ teamIds: ids });
  };

  const updateBlackout = (index, patch) => {
    update({ blackouts: form.blackouts.map((b, i) => (i === index ? { ...b, ...patch } : b)) });
  };

  const updateSlot = (index, patch) => {
    update({ slots: form.slots.map((s, i) => (i === index ? { ...s, ...patch } : s)) });
  };

  const duplicateSlot = (index) => {
    const slots = [...form.slots];
    slots.splice(index + 1, 0, { ...form.slots[index] });
    update({ slots });
  };

  const handleLoadTemplate = (templateId) => {
    setSelectedTemplateId(templateId);
    setTemplateError(null);
    setTemplateMessage(null);
    const template = templates.find((t) => String(t.id) === String(templateId));
    if (template) {
      const slots = templateToSlots(template);
      update({ slots: slots.length ? slots : [createEmptySlot()] });
    }
  };

  const openTemplateDialog = () => {
    setTemplateError(null);
    setTemplateMessage(null);
    setTemplateName(selectedTemplate?.name || '');
    setOverwriteTemplate(Boolean(selectedTemplate));
    setTemplateDialogOpen(true);
  };

  const handleSaveTemplate = async () => {
    const name = templateName.trim();
    if (!name) {
      setTemplateError('Enter a template name.');
      return;
    }
    if (form.slots.length === 0 || form.slots.some((s) => !s.dayOfWeek || !s.startTime || !s.venueId)) {
      setTemplateError('Each weekly slot needs a day, time and venue before it can be saved as a template.');
      return;
    }
    setTemplateSaving(true);
    setTemplateError(null);
    try {
      const dto = { name, slots: slotsToPayload(form.slots) };
      const saved = selectedTemplate && overwriteTemplate
        ? await unifiedApi.updateScheduleSlotTemplate(selectedTemplate.id, dto)
        : await unifiedApi.createScheduleSlotTemplate(dto);
      setTemplateDialogOpen(false);
      setTemplateMessage(`Template "${saved?.name || name}" saved.`);
      if (saved?.id) setSelectedTemplateId(String(saved.id));
      if (onTemplatesChanged) await onTemplatesChanged();
    } catch (err) {
      const message = err?.message || '';
      setTemplateError(/403|forbidden/i.test(message)
        ? 'You do not have permission to save slot templates.'
        : (message || 'Failed to save template.'));
    } finally {
      setTemplateSaving(false);
    }
  };

  const includeRegularSeason = isRegularSeasonIncluded(form);
  const allSelected = teams.length > 0 && teams.every((t) => form.teamIds.includes(t.id));

  return (
    <Stack spacing={3}>
      <Section title="Schedule name">
        <TextField
          label="Name"
          value={form.name}
          onChange={(e) => update({ name: e.target.value })}
          disabled={disabled}
          fullWidth
          required
          slotProps={{ htmlInput: { maxLength: 100 } }}
        />
        <FormControlLabel
          sx={{ mt: 1 }}
          control={(
            <Switch
              checked={includeRegularSeason}
              onChange={(e) => update({ includeRegularSeason: e.target.checked })}
              disabled={disabled}
            />
          )}
          label="Include regular season"
        />
        <Typography variant="body2" color="text.secondary">
          {includeRegularSeason
            ? 'Generates a balanced round robin from the teams, dates and weekly slots below.'
            : 'Only the playoff and other games below are created.'}
        </Typography>
      </Section>

      {includeRegularSeason && (
        <>
          <Section
            title="Teams"
            description={`${form.teamIds.length} of ${teams.length} teams selected`}
            actions={(
              <Stack direction="row" spacing={1}>
                <Button size="small" onClick={() => update({ teamIds: teams.map((t) => t.id) })} disabled={disabled || allSelected}>
                  Select all
                </Button>
                <Button size="small" onClick={() => update({ teamIds: [] })} disabled={disabled || form.teamIds.length === 0}>
                  Select none
                </Button>
              </Stack>
            )}
          >
            {teams.length === 0 ? (
              <Alert severity="info">This league has no teams yet. Add teams before generating a schedule.</Alert>
            ) : (
              <Grid container spacing={0}>
                {teams.map((team) => (
                  <Grid key={team.id} size={{ xs: 12, sm: 6, md: 4 }}>
                    <FormControlLabel
                      control={(
                        <Checkbox
                          checked={form.teamIds.includes(team.id)}
                          onChange={() => toggleTeam(team.id)}
                          disabled={disabled}
                        />
                      )}
                      label={team.name}
                      sx={{ wordBreak: 'break-word' }}
                    />
                  </Grid>
                ))}
              </Grid>
            )}
          </Section>

          <Section
            title="Dates"
            description="Week 1 starts on the start date. Enter the number of game weeks, an end date, or both (weeks are capped by the end date)."
          >
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Start date"
                type="date"
                value={form.startDate}
                onChange={(e) => update({ startDate: e.target.value })}
                disabled={disabled}
                required
                fullWidth
                slotProps={shrinkLabelProps}
              />
              <TextField
                label="Number of game weeks"
                type="number"
                value={form.numberOfWeeks}
                onChange={(e) => update({ numberOfWeeks: e.target.value })}
                disabled={disabled}
                fullWidth
                slotProps={{ ...shrinkLabelProps, htmlInput: { min: 1, max: 60 } }}
              />
              <TextField
                label="End date"
                type="date"
                value={form.endDate}
                onChange={(e) => update({ endDate: e.target.value })}
                disabled={disabled}
                fullWidth
                slotProps={shrinkLabelProps}
              />
            </Stack>
          </Section>

          <Section
            title="Blackout dates"
            description="Weeks with a game day inside a blackout are skipped."
            actions={(
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => update({ blackouts: [...form.blackouts, createEmptyBlackout()] })}
                disabled={disabled}
              >
                Add blackout
              </Button>
            )}
          >
            {form.blackouts.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No blackout dates.</Typography>
            ) : (
              <Stack spacing={2}>
                {form.blackouts.map((blackout, index) => (
                  <Stack
                    key={index}
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={2}
                    sx={{ alignItems: { xs: 'stretch', sm: 'center' } }}
                  >
                    <TextField
                      label="Blackout start"
                      type="date"
                      value={blackout.startDate}
                      onChange={(e) => updateBlackout(index, { startDate: e.target.value })}
                      disabled={disabled}
                      required
                      fullWidth
                      slotProps={shrinkLabelProps}
                    />
                    <TextField
                      label="Blackout end (optional)"
                      type="date"
                      value={blackout.endDate}
                      onChange={(e) => updateBlackout(index, { endDate: e.target.value })}
                      disabled={disabled}
                      fullWidth
                      slotProps={shrinkLabelProps}
                    />
                    <TextField
                      label="Label"
                      placeholder="e.g. Christmas"
                      value={blackout.label}
                      onChange={(e) => updateBlackout(index, { label: e.target.value })}
                      disabled={disabled}
                      fullWidth
                      slotProps={{ ...shrinkLabelProps, htmlInput: { maxLength: 100 } }}
                    />
                    <Box sx={{ alignSelf: { xs: 'flex-end', sm: 'center' } }}>
                      <Tooltip title="Remove blackout">
                        <span>
                          <IconButton
                            aria-label={`Remove blackout ${index + 1}`}
                            onClick={() => update({ blackouts: form.blackouts.filter((_, i) => i !== index) })}
                            disabled={disabled}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </Stack>
                ))}
              </Stack>
            )}
          </Section>

          <Section
            title="Weekly game slots"
            description="Each row is one game per week. Add a row for every game played in a typical week."
          >
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2, alignItems: { xs: 'stretch', sm: 'center' } }}>
              <TextField
                label="Load template"
                select
                value={selectedTemplateId}
                onChange={(e) => handleLoadTemplate(e.target.value)}
                disabled={disabled || templates.length === 0}
                sx={{ minWidth: { xs: '100%', sm: 240 } }}
                slotProps={nativeSelectProps}
                helperText={templates.length === 0 ? 'No saved templates yet' : 'Replaces the slots below'}
              >
                <option value="">{templates.length === 0 ? 'No templates' : 'Choose a template…'}</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </TextField>
              <Button
                variant="outlined"
                startIcon={<SaveIcon />}
                onClick={openTemplateDialog}
                disabled={disabled}
              >
                Save as template
              </Button>
            </Stack>

            {templateMessage && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setTemplateMessage(null)}>{templateMessage}</Alert>}
            {templateError && !templateDialogOpen && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setTemplateError(null)}>{templateError}</Alert>
            )}

            <Stack spacing={2}>
              {form.slots.map((slot, index) => {
                const venue = venueById.get(String(slot.venueId));
                const fields = venue?.fields || [];
                return (
                  <Paper
                    key={index}
                    variant="outlined"
                    sx={{ p: { xs: 1.5, sm: 1 }, bgcolor: 'grey.50' }}
                    data-testid="schedule-slot-row"
                  >
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ alignItems: { xs: 'stretch', md: 'center' } }}>
                      <TextField
                        label="Day"
                        select
                        value={slot.dayOfWeek}
                        onChange={(e) => updateSlot(index, { dayOfWeek: e.target.value })}
                        disabled={disabled}
                        fullWidth
                        slotProps={{ ...nativeSelectProps, htmlInput: { 'aria-label': `Slot ${index + 1} day` } }}
                      >
                        {DAYS_OF_WEEK.map((d) => (
                          <option key={d.value} value={d.value}>{d.label}</option>
                        ))}
                      </TextField>
                      <TextField
                        label="Start time"
                        type="time"
                        value={slot.startTime}
                        onChange={(e) => updateSlot(index, { startTime: e.target.value })}
                        disabled={disabled}
                        fullWidth
                        slotProps={{ ...shrinkLabelProps, htmlInput: { step: 300, 'aria-label': `Slot ${index + 1} start time` } }}
                      />
                      <TextField
                        label="Venue"
                        select
                        value={slot.venueId}
                        onChange={(e) => updateSlot(index, { venueId: e.target.value, fieldId: '' })}
                        disabled={disabled}
                        fullWidth
                        slotProps={{ ...nativeSelectProps, htmlInput: { 'aria-label': `Slot ${index + 1} venue` } }}
                      >
                        <option value="">Choose a venue…</option>
                        {venues.map((v) => (
                          <option key={v.id} value={v.id}>{v.name}</option>
                        ))}
                      </TextField>
                      <TextField
                        label="Field (optional)"
                        select
                        value={slot.fieldId}
                        onChange={(e) => updateSlot(index, { fieldId: e.target.value })}
                        disabled={disabled || !venue || fields.length === 0}
                        fullWidth
                        slotProps={{ ...nativeSelectProps, htmlInput: { 'aria-label': `Slot ${index + 1} field` } }}
                      >
                        <option value="">{venue && fields.length === 0 ? 'No fields' : 'Any field'}</option>
                        {fields.map((f) => (
                          <option key={f.id} value={f.id}>{f.name}</option>
                        ))}
                      </TextField>
                      <Stack direction="row" spacing={0.5} sx={{ justifyContent: 'flex-end' }}>
                        <Tooltip title="Duplicate slot">
                          <span>
                            <IconButton aria-label={`Duplicate slot ${index + 1}`} onClick={() => duplicateSlot(index)} disabled={disabled}>
                              <DuplicateIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Remove slot">
                          <span>
                            <IconButton
                              aria-label={`Remove slot ${index + 1}`}
                              onClick={() => update({ slots: form.slots.filter((_, i) => i !== index) })}
                              disabled={disabled || form.slots.length <= 1}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
            <Button
              sx={{ mt: 2 }}
              startIcon={<AddIcon />}
              onClick={() => update({ slots: [...form.slots, createEmptySlot()] })}
              disabled={disabled}
            >
              Add slot
            </Button>
          </Section>
        </>
      )}

      <ExtraGamesSection
        games={form.extraGames || []}
        onChange={(extraGames) => update({ extraGames })}
        teams={teams}
        venues={venues}
        gameLengthMinutes={form.gameLengthMinutes}
        disabled={disabled}
      />

      <Section title="Rules">
        <Grid container spacing={2}>
          {includeRegularSeason && (
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                label="Games per team per week"
                type="number"
                value={form.gamesPerTeamPerWeek}
                onChange={(e) => update({ gamesPerTeamPerWeek: e.target.value })}
                disabled={disabled}
                fullWidth
                helperText="1 to 3"
                slotProps={{ ...shrinkLabelProps, htmlInput: { min: 1, max: 3 } }}
              />
            </Grid>
          )}
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField
              label="Game length (minutes)"
              type="number"
              value={form.gameLengthMinutes}
              onChange={(e) => update({ gameLengthMinutes: e.target.value })}
              disabled={disabled}
              fullWidth
              helperText="Used for game end times and field clash checks (15 to 300)"
              slotProps={{ ...shrinkLabelProps, htmlInput: { min: 15, max: 300 } }}
            />
          </Grid>
          {includeRegularSeason && (
            <>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  label="Max consecutive weeks in same time slot"
                  type="number"
                  value={form.maxConsecutiveSameTime}
                  onChange={(e) => update({ maxConsecutiveSameTime: e.target.value })}
                  disabled={disabled}
                  fullWidth
                  helperText="Applies as entered to time slots holding half or fewer of a week's games. A time slot with most of the week's games (e.g. 3 of 5 at 8:30 pm) is relaxed automatically; the Team stats tab shows the effective limit."
                  slotProps={{ ...shrinkLabelProps, htmlInput: { min: 1, max: 60 } }}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <FormControlLabel
                  control={(
                    <Switch
                      checked={Boolean(form.flipHomeAwayOnRepeat)}
                      onChange={(e) => update({ flipHomeAwayOnRepeat: e.target.checked })}
                      disabled={disabled}
                    />
                  )}
                  label="Flip home/away when teams meet again"
                />
              </Grid>
            </>
          )}
        </Grid>
      </Section>

      <Dialog open={templateDialogOpen} onClose={() => !templateSaving && setTemplateDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Save slots as template</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {templateError && <Alert severity="error">{templateError}</Alert>}
            <TextField
              label="Template name"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              fullWidth
              autoFocus
              slotProps={{ htmlInput: { maxLength: 100 } }}
            />
            {selectedTemplate && (
              <FormControlLabel
                control={(
                  <Switch
                    checked={overwriteTemplate}
                    onChange={(e) => setOverwriteTemplate(e.target.checked)}
                  />
                )}
                label={`Update "${selectedTemplate.name}" instead of creating a new template`}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemplateDialogOpen(false)} disabled={templateSaving}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveTemplate} disabled={templateSaving}>
            {templateSaving ? 'Saving…' : 'Save template'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default ScheduleConfigForm;
